import { Link } from 'react-router-dom';
import { Mail, Package, Truck, Facebook, ShoppingCart } from 'lucide-react';
import { DnaHelix } from './DnaHelix';
import { SubscribeForm } from './SubscribeForm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_35cdf555-53b2-4e27-b4cc-5ef3467d7ecf/artifacts/c54vax66_B3C5DCF3-B9AE-4C3A-8AF4-244C055AD917.png";

export const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] border-t border-[#27272A]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1">
            <img 
              src={LOGO_URL} 
              alt="Canadian Wellness & Fitness" 
              className="h-24 w-auto mb-4 object-contain"
            />
            <p className="text-gray-400 text-sm leading-relaxed max-w-md">
              Premium Canadian peptides for research and educational purposes. 
              We are committed to providing the highest quality compounds for 
              scientific advancement.
            </p>
            <div className="mt-5">
              <h4 className="font-['Oswald'] text-sm uppercase tracking-wider text-white mb-2">
                Be the First to Know
              </h4>
              <p className="text-gray-500 text-xs mb-3">Early access to deals, new products &amp; promos.</p>
              <SubscribeForm source="footer" compact />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Oswald'] text-lg uppercase tracking-wider text-white mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/" 
                  className="text-gray-400 hover:text-[#C41E3A] transition-colors text-sm"
                  data-testid="footer-link-home"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/#products" 
                  className="text-gray-400 hover:text-[#C41E3A] transition-colors text-sm"
                  data-testid="footer-link-products"
                >
                  Products
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="text-gray-400 hover:text-[#C41E3A] transition-colors text-sm"
                  data-testid="footer-link-about"
                >
                  About Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Oswald'] text-lg uppercase tracking-wider text-white mb-4">
              Contact Us
            </h4>
            <div className="flex items-center gap-2 mb-4">
              <Mail size={18} className="text-[#C41E3A]" />
              <a 
                href="mailto:canadianwellness2026@outlook.com"
                className="text-gray-400 hover:text-[#C41E3A] transition-colors text-sm break-all"
                data-testid="footer-email"
              >
                canadianwellness2026@outlook.com
              </a>
            </div>
            <a
              href="mailto:canadianwellness2026@outlook.com"
              className="inline-block bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-all duration-300"
              data-testid="footer-contact-button"
            >
              Send Email
            </a>
            <div className="mt-4">
              <a
                href="https://www.facebook.com/share/g/18dFmzugdi/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-[#1877F2] transition-colors text-sm"
                data-testid="footer-facebook-link"
              >
                <Facebook size={20} />
                <span>Follow us on Facebook</span>
              </a>
            </div>
          </div>
        </div>

        {/* How to Order Section */}
        <div className="mt-12 pt-8 border-t border-[#27272A]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* How to Place an Order */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Package size={24} className="text-[#C41E3A]" />
                <h4 className="font-['Oswald'] text-xl uppercase tracking-wider text-white">
                  How to Place an Order
                </h4>
              </div>
              <ol className="text-gray-400 text-sm space-y-3 list-decimal list-inside">
                <li>Browse our catalog and click <span className="text-[#D4AF37] font-semibold">"Add to Cart"</span> on the products you want.</li>
                <li>Go to your <span className="text-[#D4AF37] font-semibold">Cart</span> to review your items, adjust quantities, and fill in your mailing address.</li>
                <li>Click <span className="text-[#D4AF37] font-semibold">"Place Order"</span> — an email will pop up with your order details and unique order number. Send it to us.</li>
                <li>Send an Interac e-Transfer for your total to <span className="text-[#D4AF37] font-semibold">canadianwellness2026@outlook.com</span> — include your <span className="text-[#D4AF37] font-semibold">order number</span> in the message box.</li>
                <li>Once your e-Transfer has been received and confirmed, we will process your order and send you a tracking number.</li>
              </ol>
            </div>

            {/* Shipping Details */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck size={24} className="text-[#C41E3A]" />
                <h4 className="font-['Oswald'] text-xl uppercase tracking-wider text-white">
                  Shipping Details
                </h4>
              </div>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• Flat rate shipping is <span className="text-[#D4AF37] font-semibold">$20</span>.</li>
                <li>• We currently ship within <span className="text-[#D4AF37] font-semibold">Canada only</span>.</li>
                <li>• <span className="text-[#D4AF37] font-semibold">COAs available upon request</span>.</li>
              </ul>
              <p className="text-gray-400 text-sm mt-4">
                If you have any questions before ordering, feel free to reach out—we're happy to help.
              </p>
              <Link
                to="/cart"
                className="inline-flex items-center gap-2 mt-4 bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-all duration-300"
                data-testid="footer-cart-button"
              >
                <ShoppingCart size={16} />
                Go to Cart
              </Link>
            </div>
          </div>
        </div>

        {/* Disclaimer & Terms of Use */}
        <div className="mt-12 pt-8 border-t border-[#27272A]" data-testid="footer-disclaimer-terms">
          <h3 className="font-['Oswald'] text-2xl uppercase tracking-wider text-white mb-6 text-center">
            Disclaimer & <span className="text-[#D4AF37]">Terms of Use</span>
          </h3>

          <div className="space-y-6 text-gray-400 text-sm leading-relaxed max-w-4xl mx-auto">
            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Educational Use Only</h4>
              <p>All products and information provided by Canadian Wellness & Fitness are intended strictly for educational and informational purposes only. Nothing on this website is intended to diagnose, treat, cure, or prevent any disease or medical condition.</p>
              <p className="mt-2">All content, including product descriptions, research references, and customer communications, is provided for general knowledge and informational purposes only and should not be interpreted as medical advice.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">No Medical Advice</h4>
              <p>Canadian Wellness & Fitness does not provide medical advice, treatment recommendations, or individualized health protocols.</p>
              <p className="mt-2">Customers should consult a qualified healthcare professional before making any decisions related to their health or wellness.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Customer Responsibility</h4>
              <p className="mb-2">By purchasing from this website, you confirm that:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>You are at least 18 years of age</li>
                <li>You understand the nature of the products being purchased</li>
                <li>You will use all products in accordance with applicable laws and regulations</li>
                <li>You assume full responsibility for the proper handling and use of any products purchased</li>
              </ul>
              <p className="mt-2">Canadian Wellness & Fitness is not liable for misuse, improper handling, or use outside of intended research purposes.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Educational Support</h4>
              <p>We provide complimentary educational support to our customers, including general product information, publicly available research references, and best practices for storage and handling.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Limitation of Liability</h4>
              <p>Under no circumstances shall Canadian Wellness & Fitness be held liable for any direct, indirect, incidental, or consequential damages resulting from the use or misuse of any products or information provided.</p>
              <p className="mt-2">All products are used at the customer's own risk.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Accuracy of Information</h4>
              <p>While we strive to provide accurate and up-to-date information, Canadian Wellness & Fitness makes no guarantees regarding the completeness, reliability, or accuracy of any content on this website.</p>
              <p className="mt-2">Information may be updated or changed at any time without notice.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Return & Refund Policy</h4>
              <p>Due to the nature of our products, all sales are final. We do not accept returns, exchanges, or refund requests unless there is a confirmed manufacturer defect or issue with the item.</p>
              <p className="mt-2">If a manufacturer issue is found, the affected item will be replaced at our discretion. Customers must contact us within <span className="text-[#D4AF37] font-semibold">7 days</span> of receiving their order and provide photos or details of the issue so we can review the claim.</p>
              <p className="mt-2">We do not offer refunds or returns for change of mind, incorrect product selection, or items that have been opened, used, or improperly stored after delivery.</p>
              <p className="mt-2">Thank you for understanding and supporting our quality control process.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Agreement</h4>
              <p>By accessing this website and purchasing from Canadian Wellness & Fitness, you acknowledge that you have read, understood, and agreed to this disclaimer and all terms stated above.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div>
              <h4 className="text-[#D4AF37] font-semibold uppercase tracking-wide mb-2">Contact</h4>
              <p>For any questions regarding this disclaimer, please contact us directly.</p>
            </div>

            <hr className="border-[#27272A]" />

            <div className="text-center pt-2">
              <p className="text-white font-semibold">Canadian Wellness & Fitness</p>
              <p className="text-[#D4AF37]"><DnaHelix size="sm" /> Research. Education. Performance. <DnaHelix size="sm" /></p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-6 border-t border-[#27272A] flex flex-col md:flex-row justify-between items-center text-gray-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Canadian Wellness & Fitness. All rights reserved.</p>
          <p className="mt-2 md:mt-0">Premium Research Compounds</p>
          {/* Build version marker */}
          <span className="hidden" data-testid="build-version" data-version="v2.1-20260215">v2.1</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
